import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import { Badge, Button, Card } from "../components/common/UIKit";
import { captureLeadGoogleMap } from "../api/leads";
import { ArrowLeft, Mail, MapPin, Phone, Calendar, DollarSign, Home,
  User, FileText, Scale, Copy, ExternalLink, Navigation, Clock, Search as SearchIcon,
} from "lucide-react";
import { InfoRow, SectionTitle, Stat } from "./lead_detail/PartyCard";
import { MapHero, checkStreetViewAvailability } from "./lead_detail/MapHero";
import { PartiesTabs } from "./lead_detail/PartiesTabs";
import { PARTY_SHOWN_KEYS, RELATIVE_RE, buildPartiesTabs } from "./lead_detail/parties";
import {
  GOOGLE_MAPS_KEY,
  HAS_KEY,
  LEAD_CACHE_KEY,
  STATUS_LABELS,
  deriveDate,
  deriveEmails,
  deriveOwner,
  derivePhones,
  deriveStatus,
  firstNonEmpty,
  formatCurrency,
  formatPhone,
  isHiddenValue,
  prettyLabel,
} from "./lead_detail/utils";
import { extractLeadUuid } from "../utils/leadDetail";

function ExtraLookupLinks({ owner, address }) {
  const tpsUrl = owner
    ? `https://www.truepeoplesearch.com/results?name=${encodeURIComponent(owner)}${address ? `&citystatezip=${encodeURIComponent(address)}` : ""}`
    : "https://www.truepeoplesearch.com/";
  const cbcUrl = owner
    ? `https://www.cyberbackgroundchecks.com/people/${encodeURIComponent(owner)}`
    : "https://www.cyberbackgroundchecks.com/";

  return (
    <div className="mt-5 pt-5 border-t border-zinc-100" data-testid="extra-lookup-links">
      <p className="text-sm text-zinc-700 leading-relaxed">
        <span className="font-bold text-zinc-950">Not the phone numbers or emails you're looking for?</span>{" "}
        Use <LookupLink href={tpsUrl} label="True People Search" /> or{" "}
        <LookupLink href={cbcUrl} label="Cyber Background Checks" /> below to find other possible numbers and emails — 100% free.
      </p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <LookupCard
          testId="lookup-tps"
          href={tpsUrl}
          label="True People Search"
          url="https://www.truepeoplesearch.com/"
        />
        <LookupCard
          testId="lookup-cbc"
          href={cbcUrl}
          label="Cyber Background Checks"
          url="https://www.cyberbackgroundchecks.com/"
        />
      </div>
    </div>
  );
}

function LookupLink({ href, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-bold text-zinc-950 underline decoration-zinc-400 underline-offset-2 hover:decoration-zinc-950"
    >
      {label}
    </a>
  );
}

function LookupCard({ testId, href, label, url }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={testId}
      className="group flex items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-white px-3 py-2.5 hover:border-zinc-950 transition-colors"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="h-8 w-8 rounded-md bg-zinc-100 flex items-center justify-center flex-shrink-0 group-hover:bg-zinc-950 transition-colors">
          <SearchIcon className="h-3.5 w-3.5 text-zinc-700 group-hover:text-white" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-zinc-950 truncate">{label}</p>
          <p className="text-xs text-zinc-500 truncate font-mono">{url}</p>
        </div>
      </div>
      <ExternalLink className="h-4 w-4 text-zinc-400 group-hover:text-zinc-950 flex-shrink-0" />
    </a>
  );
}


export default function LeadDetailPage() {
  const { leadId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [lead, setLead] = useState(location.state?.lead || null);
  const [copied, setCopied] = useState(null);
  const [mapImageUrl, setMapImageUrl] = useState(location.state?.lead?.google_map_image_url || null);
  const [mapImageLoading, setMapImageLoading] = useState(false);
  const uploadLeadId = useMemo(() => extractLeadUuid(lead?.id) || extractLeadUuid(leadId), [lead, leadId]);

  useEffect(() => {
    if (location.state?.lead && leadId) {
      try {
        const raw = sessionStorage.getItem(LEAD_CACHE_KEY);
        const cache = raw ? JSON.parse(raw) : {};
        cache[leadId] = location.state.lead;
        sessionStorage.setItem(LEAD_CACHE_KEY, JSON.stringify(cache));
      } catch (e) { /* ignore */ }
      return;
    }
    if (!leadId) return;
    try {
      const raw = sessionStorage.getItem(LEAD_CACHE_KEY);
      const cache = raw ? JSON.parse(raw) : {};
      if (cache[leadId]) setLead(cache[leadId]);
    } catch (e) { /* ignore */ }
  }, [location.state, leadId]);

  useEffect(() => {
    setMapImageUrl(lead?.google_map_image_url || null);
  }, [lead]);

  useEffect(() => {
    const persistLead = (nextLead) => {
      if (!leadId || !nextLead) return;
      try {
        const raw = sessionStorage.getItem(LEAD_CACHE_KEY);
        const cache = raw ? JSON.parse(raw) : {};
        cache[leadId] = nextLead;
        sessionStorage.setItem(LEAD_CACHE_KEY, JSON.stringify(cache));
      } catch (e) { /* ignore */ }
    };

    const captureMapImage = async () => {
      if (!uploadLeadId || !lead || !HAS_KEY) return;
      const existingUrl = String(lead.google_map_image_url || "").trim();
      const currentMapUrl = String(mapImageUrl || "").trim();
      if (existingUrl || currentMapUrl) return;

      const addressCenter = [
        firstNonEmpty(lead.property_address, lead.address, lead.mailing_address),
        firstNonEmpty(lead.property_city, lead.city, lead.mailing_city),
        firstNonEmpty(lead.state, lead.property_state, lead.mailing_state),
        firstNonEmpty(lead.property_zip, lead.zip, lead.mailing_zip),
      ].filter(Boolean).join(", ");
      const center = [
        lead.latitude && lead.longitude ? `${lead.latitude},${lead.longitude}` : null,
        addressCenter,
      ].find(Boolean);
      if (!center) return;

      // Skip the Google static-map upload entirely if Street View confirms Google
      // has no imagery for this address — avoids saving a "Sorry, we have no
      // imagery here" placeholder as the lead's hero image.
      try {
        const svStatus = await checkStreetViewAvailability(addressCenter || center);
        if (svStatus === "ZERO_RESULTS" || svStatus === "NOT_FOUND") return;
      } catch (_e) { /* best-effort — fall through to capture */ }

      setMapImageLoading(true);
      try {
        const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(center)}&zoom=15&size=600x400&key=${GOOGLE_MAPS_KEY}`;
        const imageResp = await fetch(staticMapUrl);
        if (!imageResp.ok) throw new Error("Failed to fetch static map");
        const blob = await imageResp.blob();
        const file = new File([blob], `lead-map-${uploadLeadId}.png`, { type: blob.type || "image/png" });
        const uploadResp = await captureLeadGoogleMap(uploadLeadId, file);
        const uploadedUrl = uploadResp?.google_map_image_url;
        if (uploadedUrl) {
          setMapImageUrl(uploadedUrl);
          const nextLead = { ...lead, google_map_image_url: uploadedUrl };
          setLead(nextLead);
          persistLead(nextLead);
        }
      } catch (e) {
        // Ignore silently so the detail page still renders with the live map fallback.
      } finally {
        setMapImageLoading(false);
      }
    };

    captureMapImage();
  }, [uploadLeadId, lead, mapImageUrl]);

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) { /* ignore */ }
  };

  const statusMeta = useMemo(() => {
    const raw = deriveStatus(lead || {});
    const key = (raw || "").toString().toLowerCase().replace(/ /g, "_");
    return STATUS_LABELS[key] || (raw ? { label: String(raw).toUpperCase(), color: "zinc" } : null);
  }, [lead]);

  const d = useMemo(() => {
    if (!lead) return null;
    const street = firstNonEmpty(lead.property_address, lead.address, lead.mailing_address);
    const city = firstNonEmpty(lead.property_city, lead.city, lead.mailing_city);
    const state = firstNonEmpty(lead.state, lead.property_state, lead.mailing_state);
    const zip = firstNonEmpty(lead.property_zip, lead.zip, lead.mailing_zip);
    return {
      owner: deriveOwner(lead),
      phones: derivePhones(lead),
      emails: deriveEmails(lead),
      date: deriveDate(lead),
      street, city, state, zip,
      fullAddress: [street, city, state, zip].filter(Boolean).join(", "),
      county: firstNonEmpty(lead.county_name, lead.county),
      price: formatCurrency(lead.price ?? lead.list_price),
      amountDue: formatCurrency(lead.amount_due ?? lead.balance_due),
      beds: firstNonEmpty(lead.bedrooms, lead.beds),
      baths: firstNonEmpty(lead.bathrooms, lead.baths),
      sqft: firstNonEmpty(lead.sqft, lead.square_feet),
      yearBuilt: firstNonEmpty(lead.year_built),
      propertyUse: firstNonEmpty(lead.property_use, lead.property_type),
      institution: firstNonEmpty(lead.institution_name, lead.lender_name),
      caseNumber: firstNonEmpty(lead.case_number, lead.docket_number),
    };
  }, [lead]);

  const parties = useMemo(() => buildPartiesTabs(lead), [lead]);


const HIDDEN_EXTRA_KEYS = [
  "google_map_image_url",
  // add more keys here as needed
];

 const additionalEntries = useMemo(() => {
    if (!lead) return [];
    const shown = new Set(PARTY_SHOWN_KEYS);
    return Object.entries(lead)
      .filter(([k, v]) => {
        if (shown.has(k)) return false;
        if (HIDDEN_EXTRA_KEYS.includes(k)) return false;
        if (RELATIVE_RE.test(k)) return false;
        if (isHiddenValue(k, v)) return false;
        if (v === null || v === undefined || String(v).trim() === "" || String(v).toLowerCase() === "nan") return false;
        return true;
      })
      .sort(([a], [b]) => a.localeCompare(b));
  }, [lead]);

  if (!lead) {
    return (
      <AppShell title="Lead Details">
        <div className="p-8">
          <Card className="max-w-2xl">
            <p className="text-sm text-zinc-600">This lead detail is not available anymore. Open it again from the leads table.</p>
            <div className="mt-4">
              <Button variant="outline" onClick={() => navigate("/leads/results")}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to leads
              </Button>
            </div>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="bg-zinc-50 min-h-full">
        {/* Top bar */}
        <div className="bg-white border-b border-zinc-200 px-8 py-4 flex items-center justify-between sticky top-0 z-20">
          <Link to="/leads/results" className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-950" data-testid="lead-detail-back">
            <ArrowLeft className="h-4 w-4" /> Back to leads
          </Link>
          <div className="flex items-center gap-2">
            {d?.phones?.[0] && (
              <a href={`tel:${String(d.phones[0]).replace(/\D/g, "")}`} className="hidden md:inline-flex">
                <Button className="h-9"><Phone className="h-4 w-4 mr-1.5" /> Call</Button>
              </a>
            )}
            {d?.emails?.[0] && (
              <a href={`mailto:${d.emails[0]}`} className="hidden md:inline-flex">
                <Button variant="outline" className="h-9"><Mail className="h-4 w-4 mr-1.5" /> Email</Button>
              </a>
            )}
          </div>
        </div>

        <div className="px-4 py-6 sm:px-6 lg:px-8 space-y-6 w-full">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-stretch" data-testid="lead-detail-hero">
            <div className="rounded-md border border-zinc-200 bg-white shadow-sm p-6 lg:p-7">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {statusMeta ? <Badge color={statusMeta.color}>{statusMeta.label}</Badge> : null}
                {d?.date && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-zinc-500">
                    <Calendar className="h-3.5 w-3.5" /> {d.date}
                  </span>
                )}
                {d?.county && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-zinc-500">
                    {d.county}{d.state ? `, ${d.state}` : ""}
                  </span>
                )}
              </div>
              <h1
                className="text-3xl lg:text-4xl font-black tracking-tight text-zinc-950 mb-3"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                data-testid="lead-detail-owner"
              >
                {d?.owner || "Unknown Owner"}
              </h1>
              {d?.fullAddress && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(d.fullAddress)}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-start gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-950 transition-colors"
                  data-testid="lead-detail-address-link"
                >
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{d.fullAddress}</span>
                  <ExternalLink className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                </a>
              )}
              <div className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-4 pt-5 border-t border-zinc-100">
                <Stat label="Price" value={d?.price} accent="text-green-700" />
                <Stat label="Amount Due" value={d?.amountDue} accent="text-amber-700" />
                <Stat label="Beds / Baths" value={d?.beds || d?.baths ? `${d.beds || "—"} / ${d.baths || "—"}` : null} />
                <Stat label="Sqft" value={d?.sqft ? Number(d.sqft).toLocaleString() : null} />
              </div>
            </div>

            <MapHero
              address={d?.fullAddress}
              ownerLabel={d?.owner}
              imageUrl={mapImageUrl}
              imageLoading={mapImageLoading}
              compact
              className="h-full"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column: Parties (by lead type) + Property */}
            <div className="lg:col-span-2 space-y-6">
              {parties.length > 0 && (
                <Card>
                  <SectionTitle icon={User} title={`${statusMeta?.label || "Lead"} — Parties & Contacts`} />
                  <PartiesTabs tabs={parties} copy={copy} copied={copied} />
                  <ExtraLookupLinks owner={d?.owner} address={d?.fullAddress} />
                </Card>
              )}
              {parties.length === 0 && (d?.phones?.length > 0 || d?.emails?.length > 0) && (
                <Card>
                  <SectionTitle icon={User} title="Contact" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {d.phones.map((raw, i) => {
                      const digits = String(raw).replace(/\D/g, "");
                      return (
                        <div key={`p-${i}`} className="flex items-center justify-between gap-2 rounded-md border border-zinc-200 bg-white p-3 hover:border-zinc-950 transition-colors" data-testid={`lead-phone-${i}`}>
                          <a href={`tel:${digits}`} className="flex items-center gap-2.5 min-w-0 flex-1 group">
                            <div className="h-9 w-9 rounded-md bg-green-50 flex items-center justify-center flex-shrink-0 group-hover:bg-green-100">
                              <Phone className="h-4 w-4 text-green-700" strokeWidth={2.5} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Phone</p>
                              <p className="font-mono text-sm font-bold text-zinc-950 truncate group-hover:text-green-700">
                                {formatPhone(raw)}
                              </p>
                            </div>
                          </a>
                          <button onClick={() => copy(digits, `phone-${i}`)} className="p-2 rounded-md text-zinc-400 hover:text-zinc-950 hover:bg-zinc-50" title="Copy">
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                          {copied === `phone-${i}` && <span className="text-[10px] font-bold text-green-700">Copied!</span>}
                        </div>
                      );
                    })}
                    {d.emails.map((email, i) => (
                      <div key={`e-${i}`} className="flex items-center justify-between gap-2 rounded-md border border-zinc-200 bg-white p-3 hover:border-zinc-950 transition-colors" data-testid={`lead-email-${i}`}>
                        <a href={`mailto:${email}`} className="flex items-center gap-2.5 min-w-0 flex-1 group">
                          <div className="h-9 w-9 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100">
                            <Mail className="h-4 w-4 text-blue-700" strokeWidth={2.5} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Email</p>
                            <p className="text-sm font-bold text-zinc-950 truncate group-hover:text-blue-700">{email}</p>
                          </div>
                        </a>
                        <button onClick={() => copy(email, `email-${i}`)} className="p-2 rounded-md text-zinc-400 hover:text-zinc-950 hover:bg-zinc-50" title="Copy">
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        {copied === `email-${i}` && <span className="text-[10px] font-bold text-green-700">Copied!</span>}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {(d?.fullAddress || d?.beds || d?.baths || d?.sqft) && (
                <Card>
                  <SectionTitle icon={Home} title="Property" />
                  <InfoRow label="Address" value={d.fullAddress} />
                  <InfoRow label="County" value={d.county} />
                  {d.propertyUse && <InfoRow label="Type" value={d.propertyUse} />}
                  {d.yearBuilt && <InfoRow label="Year built" value={String(d.yearBuilt).match(/\d{4}/)?.[0] || d.yearBuilt} />}
                </Card>
              )}

              {additionalEntries.length > 0 && (
                <Card>
                  <SectionTitle icon={FileText} title="Additional details" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    {additionalEntries.map(([key, value]) => (
                      <InfoRow
                        key={key}
                        label={prettyLabel(key)}
                        value={typeof value === "object" ? JSON.stringify(value) : String(value)}
                        testId={`lead-extra-${key}`}
                      />
                    ))}
                  </div>
                </Card>
              )}
            </div>

            {/* Right column: Financial + Legal + Quick Actions */}
            <div className="space-y-6">
              {(d?.price || d?.amountDue || d?.institution) && (
                <Card>
                  <SectionTitle icon={DollarSign} title="Financial" />
                  <div className="space-y-3">
                    {d.price && (
                      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">List price</p>
                        <p className="text-xl font-black tracking-tight text-zinc-950 tabular-nums">{d.price}</p>
                      </div>
                    )}
                    {d.amountDue && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-1">Amount due</p>
                        <p className="text-xl font-black tracking-tight text-amber-900 tabular-nums">{d.amountDue}</p>
                      </div>
                    )}
                    {d.institution && <InfoRow label="Institution" value={d.institution} />}
                  </div>
                </Card>
              )}

              {(d?.caseNumber || statusMeta?.label) && (
                <Card>
                  <SectionTitle icon={Scale} title="Legal" />
                  <InfoRow label="Status" value={statusMeta?.label} />
                  <InfoRow label="Case #" value={d.caseNumber} />
                </Card>
              )}

              <Card>
                <SectionTitle icon={Clock} title="Quick Actions" />
                <div className="space-y-2">
                  {d?.phones?.[0] && (
                    <a href={`tel:${String(d.phones[0]).replace(/\D/g, "")}`} className="block">
                      <Button className="w-full"><Phone className="h-4 w-4 mr-1.5" /> Call {formatPhone(d.phones[0])}</Button>
                    </a>
                  )}
                  {d?.emails?.[0] && (
                    <a href={`mailto:${d.emails[0]}`} className="block" data-testid="quick-action-email">
                      <Button variant="outline" className="w-full">
                        <Mail className="h-4 w-4 mr-1.5 flex-shrink-0" />
                        <span className="truncate">Email {d.emails[0]}</span>
                      </Button>
                    </a>
                  )}
                  {d?.fullAddress && (
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(d.fullAddress)}`} target="_blank" rel="noreferrer" className="block">
                      <Button variant="outline" className="w-full"><Navigation className="h-4 w-4 mr-1.5" /> Get directions</Button>
                    </a>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
